import React, { useState, useRef, useEffect, useCallback } from "react";
import { OnyxMascot } from "../../shared/OnyxMascot";
import { useI18n } from "../i18n";

// ─── Interactive Demo Chat ───────────────────────────────────────────────────

interface DemoMessage {
  id: string;
  role: "user" | "assistant";
  content: string;
}

const SUGGESTED_QUESTIONS = [
  "What is Onyx AI?",
  "Berapa harga?",
  "How do I get started?",
  "支持中文吗？",
];

function DemoChat() {
  const { t } = useI18n();
  const [messages, setMessages] = useState<DemoMessage[]>([]);
  const [input, setInput] = useState("");
  const [isStreaming, setIsStreaming] = useState(false);
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (messages.length === 0) return;
    const container = messagesContainerRef.current;
    if (container) {
      container.scrollTop = container.scrollHeight;
    }
  }, [messages]);

  const sendMessage = useCallback(
    async (text: string) => {
      if (!text.trim() || isStreaming) return;

      const userMsg: DemoMessage = {
        id: `u-${Date.now()}`,
        role: "user",
        content: text.trim(),
      };

      const assistantId = `a-${Date.now()}`;
      setMessages((prev) => [
        ...prev,
        userMsg,
        { id: assistantId, role: "assistant", content: "" },
      ]);
      setInput("");
      setIsStreaming(true);

      try {
        const res = await fetch("/api/demo/chat", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            message: text.trim(),
            history: messages.slice(-6).map((m) => ({
              role: m.role,
              content: m.content,
            })),
          }),
        });

        if (!res.ok || !res.body) throw new Error("Failed");

        const reader = res.body.getReader();
        const decoder = new TextDecoder();
        let buffer = "";
        let fullContent = "";

        while (true) {
          const { done, value } = await reader.read();
          if (done) break;
          buffer += decoder.decode(value, { stream: true });
          const lines = buffer.split("\n");
          buffer = lines.pop() || "";

          for (const line of lines) {
            if (line.startsWith("data: ")) {
              const data = line.slice(6);
              if (data === "[DONE]") continue;
              try {
                const parsed = JSON.parse(data);
                if (parsed.content) {
                  fullContent += parsed.content;
                  setMessages((prev) =>
                    prev.map((m) =>
                      m.id === assistantId
                        ? { ...m, content: fullContent }
                        : m
                    )
                  );
                }
              } catch {}
            }
          }
        }
      } catch {
        setMessages((prev) =>
          prev.map((m) =>
            m.id === assistantId
              ? { ...m, content: "Oops, something went wrong. Try again!" }
              : m
          )
        );
      } finally {
        setIsStreaming(false);
      }
    },
    [isStreaming, messages]
  );

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      e.preventDefault();
      sendMessage(input);
    }
  };

  return (
    <div
      className="rounded-xl border border-[#2a2040] shadow-2xl overflow-hidden onyx-glow"
      style={{ background: "linear-gradient(145deg, #14121e, #1a1530)" }}
    >
      {/* Header */}
      <div
        className="px-4 py-3 border-b border-[#2a2040] flex items-center gap-2"
        style={{ background: "linear-gradient(135deg, #1a1530, #201540)" }}
      >
        <div className="w-3 h-3 rounded-full bg-[#a078d0]/40"></div>
        <div className="w-3 h-3 rounded-full bg-[#7b5ea7]/40"></div>
        <div className="w-3 h-3 rounded-full bg-[#c0a0e0]/40"></div>
        <span className="ml-2 text-xs text-[#7a6a9a]">
          {t("hero.chat.title")} — {t("hero.demo.label")}
        </span>
        {isStreaming && (
          <span className="ml-auto text-xs text-[#a078d0] animate-pulse">
            typing...
          </span>
        )}
      </div>

      {/* Messages area */}
      <div
        ref={messagesContainerRef}
        className="p-4 space-y-3 overflow-y-auto text-left"
        style={{ minHeight: "200px", maxHeight: "280px" }}
      >
        {messages.length === 0 && (
          <div className="text-center py-6">
            <p className="text-sm text-[#7a6a9a] mb-4">
              {t("hero.demo.prompt")}
            </p>
            <div className="flex flex-wrap gap-2 justify-center">
              {SUGGESTED_QUESTIONS.map((q) => (
                <button
                  key={q}
                  onClick={() => sendMessage(q)}
                  className="text-xs px-3 py-1.5 rounded-full bg-[#7b5ea7]/15 text-[#c0a0e0] border border-[#7b5ea7]/30 hover:bg-[#7b5ea7]/25 transition-colors cursor-pointer"
                >
                  {q}
                </button>
              ))}
            </div>
          </div>
        )}

        {messages.map((msg) => (
          <div
            key={msg.id}
            className={`flex ${
              msg.role === "user" ? "justify-end" : "justify-start"
            }`}
          >
            <div
              className={`text-sm px-4 py-2 max-w-[80%] whitespace-pre-wrap ${
                msg.role === "user"
                  ? "bg-[#7b5ea7] text-white rounded-xl rounded-br-sm"
                  : "bg-[#1a1230] text-[#d0c0e8] rounded-xl rounded-bl-sm border border-[#2a2040]"
              }`}
            >
              {msg.content || (
                <span className="flex gap-1">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#7a6a9a] animate-bounce" style={{ animationDelay: "0ms" }} />
                  <span className="w-1.5 h-1.5 rounded-full bg-[#7a6a9a] animate-bounce" style={{ animationDelay: "150ms" }} />
                  <span className="w-1.5 h-1.5 rounded-full bg-[#7a6a9a] animate-bounce" style={{ animationDelay: "300ms" }} />
                </span>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Input */}
      <div className="px-4 py-3 border-t border-[#2a2040] flex gap-2">
        <input
          ref={inputRef}
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={t("hero.demo.inputPlaceholder")}
          disabled={isStreaming}
          className="flex-1 bg-transparent text-sm text-[#d0c0e8] placeholder-[#5a4a7a] outline-none"
        />
        <button
          onClick={() => sendMessage(input)}
          disabled={!input.trim() || isStreaming}
          className="text-[#a078d0] hover:text-[#c0a0e0] disabled:text-[#3a2a50] transition-colors"
        >
          <svg
            width="18"
            height="18"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <line x1="22" y1="2" x2="11" y2="13" />
            <polygon points="22 2 15 22 11 13 2 9 22 2" />
          </svg>
        </button>
      </div>
    </div>
  );
}

// ─── Hero Section ────────────────────────────────────────────────────────────

export function Hero() {
  const { t } = useI18n();

  return (
    <section
      className="pt-32 pb-20 px-6 onyx-particles"
      style={{
        background: "linear-gradient(180deg, #1a1028 0%, #0a0a12 100%)",
      }}
    >
      <div className="max-w-5xl mx-auto text-center">
        <div className="inline-block px-4 py-1.5 mb-6 rounded-full bg-[#a078d0]/10 border border-[#7b5ea7]/30">
          <span className="text-sm text-[#c0a0e0] font-medium">
            {t("hero.badge")}
          </span>
        </div>

        <h1 className="text-5xl md:text-6xl font-bold text-white leading-tight mb-6">
          {t("hero.title1")}{" "}
          <span className="onyx-gradient-text onyx-title">ONYX</span>
          <br />
          <span className="text-[#c0a0e0]">{t("hero.title2")}</span>
        </h1>

        <p className="text-lg text-[#8a7a9a] max-w-2xl mx-auto mb-10 leading-relaxed">
          {t("hero.description")}
        </p>

        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <a
            href="/signup"
            className="px-8 py-3 bg-[#7b5ea7] hover:bg-[#6a4f96] text-white font-semibold rounded-lg transition-colors text-base onyx-shimmer"
          >
            {t("hero.cta1")}
          </a>
          <a
            href="#how-it-works"
            className="px-8 py-3 bg-[#1a1530] hover:bg-[#2a2040] text-[#c0a0e0] font-semibold rounded-lg transition-colors text-base border border-[#2a2040]"
          >
            {t("hero.cta2")}
          </a>
        </div>

        {/* Mascot + Interactive Demo Chat */}
        <div className="mt-16 flex flex-col md:flex-row items-center justify-center gap-8 md:gap-12">
          <div className="flex-shrink-0">
            <OnyxMascot size="large" />
          </div>

          <div className="w-full max-w-lg text-left">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 mb-3 rounded-full bg-[#7b5ea7]/20 border border-[#7b5ea7]/40">
              <span className="w-2 h-2 rounded-full bg-[#a078d0] animate-pulse"></span>
              <p className="text-sm text-[#c0a0e0] font-semibold tracking-wide">
                {t("hero.demo.tryIt")}
              </p>
            </div>
            <DemoChat />
            <p className="text-sm text-[#8a7a9a] mt-3 text-center">{t("hero.trusted")}</p>
          </div>
        </div>
      </div>
    </section>
  );
}
